/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { useRef } from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { useHasBeenOnScreen } from "~/lib/use-has-been-on-screen";

/**
 * Ported from bouncieweb's `Bento/AnimatedCurfewBentoItem.js`.
 *
 * A "Curfew" card that animates itself the first time it scrolls into view: a
 * toggle switch flips on (knob slides right, track turns green) and the whole
 * card inverts from light (blueLight1 / black text) to dark (zachBlack / white
 * text). The animations are staggered with delays and only fire once, driven by
 * this item's own `useHasBeenOnScreen`.
 *
 * The card owns its grid placement via the passed `className` (the parent grid
 * targets `.curfew`). Keyframes are scoped in a uniquely-named local <style>.
 * The toggle knob travels a shorter distance on mobile and a longer one at md+
 * (the original swapped keyframes at the md breakpoint) — reproduced with two
 * animations gated by a media query inside the <style> block. The staggered
 * delays are also longer at lg (the original bumped them for large screens),
 * reproduced with `.cf-curfew-*` classes overridden inside a lg media query.
 */
export default function AnimatedCurfewBentoItem({
  className,
  text,
}: {
  className?: string;
  text: string;
}) {
  const itemRef = useRef<HTMLDivElement>(null);
  const hasBeenOnScreen = useHasBeenOnScreen(itemRef);
  const animate = hasBeenOnScreen;

  return (
    <>
      <style>{`
        @keyframes cf-curfew-track {
          0%   { background: ${colors.gray2}; }
          100% { background: ${colors.green}; }
        }
        @keyframes cf-curfew-knob {
          0%   { left: 3px; }
          100% { left: 24px; }
        }
        @media (min-width: 768px) {
          @keyframes cf-curfew-knob {
            0%   { left: 3px; }
            100% { left: 35px; }
          }
        }
        @keyframes cf-curfew-card {
          0%   { background-color: ${colors.blueLight1}; color: ${colors.black}; }
          100% { background-color: ${colors.zachBlack}; color: ${colors.white}; }
        }
        .cf-curfew-toggle-anim {
          animation-name: var(--cf-curfew-toggle-name, none);
          animation-duration: 0.75s;
          animation-delay: 1.5s;
          animation-fill-mode: forwards;
          animation-timing-function: ease-out;
        }
        .cf-curfew-card-anim {
          animation-name: var(--cf-curfew-card-name, none);
          animation-duration: 1.25s;
          animation-delay: 2s;
          animation-fill-mode: forwards;
          animation-timing-function: ease-out;
        }
        @media (min-width: 1024px) {
          .cf-curfew-toggle-anim { animation-delay: 3s; }
          .cf-curfew-card-anim { animation-delay: 3.5s; }
        }
      `}</style>

      {/* CurfewItem — GridItem base: rounded, clipped, relative, responsive padding */}
      <div
        ref={itemRef}
        className={cn(
          "cf-curfew-card-anim",
          "relative flex flex-col items-center justify-start overflow-hidden rounded-[20px] text-center",
          "p-0 pt-3 text-[3.2vw] sm:text-[16px] md:p-2 lg:p-3 xl:pt-8",
          className,
        )}
        style={
          {
            backgroundColor: colors.blueLight1,
            color: colors.black,
            "--cf-curfew-card-name": animate ? "cf-curfew-card" : "none",
          } as React.CSSProperties
        }
      >
        {/* BentoGridH3Custom */}
        <h3 className="font-display mb-12 text-[22px] font-extrabold sm:mb-16 md:mb-[0.7rem] md:text-[29px] lg:mb-[1.4rem]">
          Curfew
        </h3>

        {/* TogglePath */}
        <div
          className="cf-curfew-toggle-anim relative mb-2 inline-block h-[28px] w-[50px] rounded-[19px] sm:h-[38px] sm:w-[70px]"
          style={
            {
              background: colors.gray2,
              "--cf-curfew-toggle-name": animate ? "cf-curfew-track" : "none",
            } as React.CSSProperties
          }
        >
          {/* ToggleButton (knob) */}
          <div
            className="cf-curfew-toggle-anim absolute top-[3px] left-[3px] h-[22px] w-[22px] rounded-[12px] sm:h-[32px] sm:w-[32px] sm:rounded-[16px]"
            style={
              {
                background: colors.white,
                "--cf-curfew-toggle-name": animate ? "cf-curfew-knob" : "none",
              } as React.CSSProperties
            }
          />
        </div>

        <div className="[font-family:var(--font-sans)] font-bold">{text}</div>
        <div>10:00 PM - 6:00AM</div>
      </div>
    </>
  );
}
